import {
  coerceBooleanProperty,
  type BooleanInput,
} from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  type OnDestroy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';
import { TypeGuard } from '@principle-theorem/shared';
import { isNil, isNumber, isString } from 'lodash';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  IconInputOption,
  LabeledInputOption,
  OptionInput,
} from '../forms/mat-select-options';
import { TypedFormControl } from '../forms/typed-form-group';

@Component({
  selector: 'pt-settings-menu-toggle-group-item',
  templateUrl: './settings-menu-toggle-group-item.component.html',
  styleUrls: ['./settings-menu-toggle-group-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SettingsMenuToggleGroupItemComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SettingsMenuToggleGroupItemComponent
  implements ControlValueAccessor, OnDestroy
{
  private _onDestroy$: Subject<void> = new Subject();
  private _onChange: (value?: OptionInput) => void;
  disabled$ = new BehaviorSubject<boolean>(false);
  emitEvent$ = new BehaviorSubject<boolean>(true);
  isIconOption$ = new BehaviorSubject<boolean>(false);
  options$ = new ReplaySubject<OptionInput[]>(1);
  @Input() label: string;
  @Input() tooltip?: string;
  formCtrl = new TypedFormControl<OptionInput>();

  @Input()
  set disabled(disabled: BooleanInput) {
    this.disabled$.next(coerceBooleanProperty(disabled));
  }

  @Input()
  set options(options: OptionInput[]) {
    if (options) {
      this.options$.next(options);
    }
  }

  @Input()
  set emitEvent(emitEvent: boolean) {
    if (!isNil(emitEvent)) {
      this.emitEvent$.next(emitEvent);
    }
  }

  constructor() {
    this.formCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((newValue) => {
        if (this._onChange) {
          this._onChange(newValue);
        }
      });

    this.options$.pipe(takeUntil(this._onDestroy$)).subscribe((options) => {
      this.isIconOption$.next(options.every(isIconOption));
    });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  writeValue(value: string): void {
    this.formCtrl.setValue(value ?? '', { emitEvent: this.emitEvent$.value });
  }

  registerOnChange(fn: () => void): void {
    this._onChange = fn;
  }

  registerOnTouched(_fn: () => void): void {
    //
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formCtrl.disable();
      return;
    }
    this.formCtrl.enable();
  }

  asString(item?: OptionInput): string {
    if (isString(item)) {
      return item;
    }
    if (isNumber(item)) {
      return item.toString();
    }
    if (isIconOption(item)) {
      return item.icon;
    }
    if (isLabelOption(item)) {
      return item.label;
    }
    return '';
  }

  getValue(item?: OptionInput): string | number | undefined {
    return isString(item) || isNumber(item) ? item : item?.value;
  }
}

const isIconOption = TypeGuard.interface<IconInputOption>({
  icon: isString,
  value: TypeGuard.isOneOf(isString, isNumber),
});

const isLabelOption = TypeGuard.interface<LabeledInputOption>({
  label: isString,
  value: TypeGuard.isOneOf(isString, isNumber),
});
